import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import queryString from 'query-string';
import { constant, mapObject, map, filter, has } from 'underscore';
import { Mode } from '@bingads-webui-react/app-layout-container';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withPreference } from '@bingads-webui-react/preference-context';
import { withRouter } from '@bingads-webui-campaign-react/router';
import { QueryParam } from '@bingads-webui-react/scope-service';
import { StyledCollapsibleSideBar, withResponsiveMainMenu } from '@bingads-webui-campaign-react/main-menu';
import { StyledSpinnerWithLabel } from '@bingads-webui-campaign-react/shell-ui-common';

import { ImportTypeItemList, ConnectionItemList } from './components';
import { getMainMenuStyles } from './styles/main-menu-styles';
import { getAdsAccountConnections, getMCCAccountConnections } from './mock-data/connections-data';

const importTypeItems = {
  'all-imports': {
    Id: 'all-imports',
    Type: 'All',
    glyphIconName: 'ba-ReportDocument',
  },
  'google-imports': {
    Id: 'google-imports',
    Type: 'Google',
    glyphIconName: 'ba-Configuration',
  },
  'facebook-imports': {
    Id: 'facebook-imports',
    Type: 'Facebook',
    glyphIconName: 'ba-FacebookLogo',
  },
  'amazon-imports': {
    Id: 'amazon-imports',
    Type: 'Amazon',
    glyphIconName: 'ba-Configuration',
  },
  'pinterest-imports': {
    Id: 'pinterest-imports',
    Type: 'Pinterest',
    glyphIconName: 'ba-Configuration',
  },
  'file-imports': {
    Id: 'file-imports',
    Type: 'File',
    glyphIconName: 'ba-Configuration',
  },
};


class ImportCenterMainMenuView extends React.PureComponent {
  constructor(props) {
    super(props);

    this.isMCC = !has(queryString.parse(props.location.search), 'aid');
    this.connectionPermissions = {
      googleimport: true,
      facebookimport: props.permissions.IsFacebookImportEnabled,
      amazonImport: props.permissions.IsAmazonImportEnabled,
      pinterestImport: true,
    };

    this.state = {
      connections: [],
      loadingData: true,
    };

    this.fetchConnectionData().then((connections) => {
      // handle
      const handledConnections = this.addConnectionProperty(connections);
      // cache all connections
      this.allConnections = handledConnections;

      // filter by route
      const currentType = this.props.location.pathname.split('/')[4];
      let finalConnections;
      if (currentType !== 'all') {
        finalConnections = filter(handledConnections, connection => connection.Type.toLowerCase() === currentType);
      } else {
        finalConnections = this.allConnections;
      }
      this.setState({
        loadingData: false,
        connections: finalConnections,
      });
    });
  }

  onImportTypeChange = (node) => {
    // route
    this.props.onImportTypeItemClick(node);

    // get connections
    let connections = this.allConnections;
    // filter the connection list
    if (node && node.Type !== 'All') {
      connections = filter(connections, connection => connection.Type === node.Type);
    }
    this.setState({
      connections,
    });
  };

  onConnectionChange = (node) => {
    // route
    this.props.onConnectionItemClick(node);

    const connections = this.addConnectionProperty(this.state.connections);
    this.setState({
      connections,
    });
  };

  fetchConnectionData = () => {
    if (this.isMCC) {
      return getMCCAccountConnections(
        this.props.serverContext,
        queryString.parse(this.props.location.search)
      );
    }
    return getAdsAccountConnections(
      this.connectionPermissions,
      this.props.serverContext,
      queryString.parse(this.props.location.search)
    );
  };

  generateStaticItems = () => {
    const { siteMap, i18n } = this.props;

    return siteMap.map(item => ({
      ...importTypeItems[item.id],
      Name: i18n.getString(item.name),
      routePrefix: item.routePrefix,
      route: item.route,
      isSelected: item.selected,
    }));
  };

  addConnectionProperty = (connections) => {
    const { scope } = this.props;
    const connectionId = scope.values.connectionId ? scope.values.connectionId : null;

    return map(connections, item => ({
      ...item,
      isSelected: connectionId === item.Id,
    }));
  };

  renderDivider() {
    const { classes } = this.props;
    return (<div className={classNames('toggle-menu-item-divider', classes.divider)} />);
  }

  render() {
    const {
      classes,
      mode,
      i18n,
      onToggle,
      isPanelMode,
    } = this.props;

    const isExpanded = isPanelMode || mode === Mode.Expanded;

    const connectionViewContainerClassName = classNames([
      classes.mainMenuContainer,
      isExpanded ? 'ui-next-connection-view' : [classes.hidden, 'ui-next-hidden-connection-view'],
    ]);

    return (
      <StyledCollapsibleSideBar
        mode={mode}
        isPanelMode={isPanelMode}
        onToggle={onToggle}
        i18n={i18n}
      >
        <ImportTypeItemList
          isExpanded={isExpanded}
          items={this.generateStaticItems()}
          onMenuItemClick={this.onImportTypeChange}
          classList={['top-menu-item-list']}
        />

        {this.renderDivider()}

        <div className={connectionViewContainerClassName}>
          {this.state.loadingData ?
            <div className={classes.connectionLoading}>
              <StyledSpinnerWithLabel label={i18n.getString(_TL_('Loading...'))} />
            </div> :
            <ConnectionItemList
              isExpanded={isExpanded}
              items={this.state.connections}
              onConnectionItemClick={this.onConnectionChange}
            />}
        </div>
      </StyledCollapsibleSideBar>
    );
  }
}

ImportCenterMainMenuView.propTypes = {
  classes: PropTypes.shape(mapObject(
    getMainMenuStyles({}, {}),
    constant(PropTypes.string.isRequired)
  )).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
  mode: PropTypes.string.isRequired,
  onToggle: PropTypes.func.isRequired,
  onImportTypeItemClick: PropTypes.func.isRequired,
  onConnectionItemClick: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  permissions: PropTypes.shape({
    IsFacebookImportEnabled: PropTypes.bool.isRequired,
    IsAmazonImportEnabled: PropTypes.bool.isRequired,
  }).isRequired,
  scope: PropTypes.instanceOf(QueryParam).isRequired,
  isPanelMode: PropTypes.bool.isRequired,
  siteMap: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
  ]).isRequired,
  serverContext: PropTypes.shape({
    CurrentAccount: PropTypes.shape({}),
  }).isRequired,
};

export const StyledImportCenterMainMenuView = withRouter(withPreference(withResponsiveMainMenu(withDefaultStyles(ImportCenterMainMenuView, getMainMenuStyles))));
